import React, { useMemo } from 'react';
import { Badge, Flex, Text } from '@/components/ui';
import { highlightMatchingParts } from '../utils/nameHighlighting';
import { getFormDisplayName } from '@/utils/formUtils';
import { extractPathFromNestedPath } from '../utils/formTreeUtils';
import type { FileToFormMapping, SelectedFile, StudyForm } from '../types';

interface FormSelectorTriggerProps {
  selectedForm: StudyForm | null;
  availableForms: StudyForm[];
  mapping?: FileToFormMapping;
  file?: SelectedFile;
  useName?: boolean;
}

export const FormSelectorTrigger: React.FC<FormSelectorTriggerProps> = ({
  selectedForm,
  availableForms,
  mapping,
  file,
  useName = false,
}) => {
  const fileName =
    file?.source === 'uploaded' ? file.file!.name : file?.csvFile?.filename;

  // Get all parent forms in the path (excluding the selected form itself)
  const parentForms = useMemo(() => {
    if (!selectedForm) return [];

    // If nested_path exists, use it to get the full path
    if (mapping?.nested_path) {
      const path = extractPathFromNestedPath(mapping.nested_path);
      // Filter out the selected form itself and get all parent forms
      const parentFormIds = path.filter(id => id !== selectedForm.id);
      return parentFormIds
        .map(id => availableForms.find(form => form.id === id))
        .filter((form): form is StudyForm => form !== undefined);
    }

    // Fallback: find first parent (for backward compatibility)
    for (const potentialParent of availableForms) {
      if (
        potentialParent.json_schema?.subFormArray &&
        Array.isArray(potentialParent.json_schema.subFormArray)
      ) {
        if (
          potentialParent.json_schema.subFormArray.includes(selectedForm.name)
        ) {
          return [potentialParent];
        }
      }
    }
    return [];
  }, [selectedForm, availableForms, mapping?.nested_path]);

  const displayName = selectedForm
    ? useName
      ? selectedForm.name
      : getFormDisplayName(selectedForm)
    : null;

  return selectedForm ? (
    <Flex
      direction="column"
      gap="1"
      style={{ minWidth: 0, flex: 1, width: '100%' }}
    >
      <Flex align="center" gap="2" style={{ minWidth: 0, width: '100%' }}>
        {/* Breadcrumbs: Show all parent forms in the path */}
        {parentForms.length > 0 && (
          <Flex align="center" gap="1" style={{ minWidth: 0, flexShrink: 1 }}>
            {parentForms.map((parentForm, index) => (
              <React.Fragment key={parentForm.id}>
                <Text
                  size="2xsmall"
                  color="gray"
                  style={{
                    opacity: 0.5, // More muted for ancestors
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={
                    useName ? parentForm.name : getFormDisplayName(parentForm)
                  }
                >
                  {useName ? parentForm.name : getFormDisplayName(parentForm)}
                </Text>
                {index < parentForms.length - 1 && (
                  <Text size="2xsmall" color="gray" style={{ opacity: 0.4 }}>
                    →
                  </Text>
                )}
              </React.Fragment>
            ))}
            <Text size="2xsmall" color="gray" style={{ opacity: 0.5 }}>
              →
            </Text>
          </Flex>
        )}

        {/* Form name - emphasized */}
        <Flex align="center" gap="2" style={{ minWidth: 0, flex: 1 }}>
          {file ? (
            highlightMatchingParts(
              fileName || '',
              selectedForm.name, // Always use name for matching with filename
              mapping?.matchType === 'exact' || mapping?.matchType === 'fuzzy'
            )
          ) : (
            <Text
              size="small"
              weight="bold"
              highContrast
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayName}
            </Text>
          )}
          {(mapping?.matchType === 'exact' ||
            mapping?.matchType === 'fuzzy') && (
            <Badge color="blue" variant="surface" size="xsmall">
              Auto
            </Badge>
          )}
        </Flex>
      </Flex>
    </Flex>
  ) : (
    <>
      <Text size="2xsmall" weight="bold" color="warning">
        Form not selected
      </Text>
      <Text size="3xsmall" color="textSecondary">
        Click to select form
      </Text>
    </>
  );
};
